import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  List,
  MapPin,
  Rows3,
} from "lucide-react";
import { byDay, gridWindow, monthGrid, shiftMonth, weekGrid } from "@omina/core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { usePickedEvents } from "@/lib/queries";

/**
 * The calendar — the rooms she said yes to.
 *
 * This replaced `/map`, which was a page of composed rooms per track and
 * therefore a second radar. The two read as the same list twice, so the radar
 * absorbed the deciding and this slot became the thing the radar was missing: a
 * place where approving something visibly lands.
 *
 * **It shows only what she picked.** Not what is on in the city — that is the
 * radar's job, and a calendar filled with everything available is research
 * rather than a plan. `event_picks` is the source, written by Approve.
 *
 * Three views because the question changes with the horizon: a month answers
 * "how heavy is October", a week answers "what is this week", and a list answers
 * "what is next" without any grid arithmetic at all. The list is ordered by date,
 * which is the only ordering that makes sense for something you cannot reorder.
 */
export const Route = createFileRoute("/calendar")({
  component: CalendarPage,
});

type View = "month" | "week" | "list";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayIso(): string {
  // Local date, not `toISOString()` — that is UTC, and it makes "today" flip a
  // day early for anyone west of Greenwich after 4pm.
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
}

function CalendarPage() {
  const today = todayIso();
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [weekAnchor, setWeekAnchor] = useState(today);

  const cells = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const week = useMemo(() => weekGrid(weekAnchor), [weekAnchor]);

  /**
   * One request per view, sized to what the view shows.
   *
   * The month asks for its own grid span — including the neighbouring days it
   * displays — so a room on 31 July is not missing from the August grid that
   * shows it. The list asks for a wide forward window because "what is next" has
   * no month boundary.
   */
  const window = useMemo(() => {
    if (view === "month") return gridWindow(cells);
    if (view === "week") return { from: week[0]!, days: 7 };
    return { from: today, days: 90 };
  }, [view, cells, week, today]);

  const picked = usePickedEvents(window.days, window.from);
  const events = picked.data ?? [];
  const grouped = useMemo(() => byDay(events), [events]);

  const monthName = new Date(Date.UTC(cursor.year, cursor.month - 1, 12)).toLocaleDateString(
    undefined,
    { month: "long", year: "numeric" },
  );

  return (
    <div className="space-y-6 pb-16">
      <header className="space-y-2">
        <p className="flex items-center gap-2 text-2xs tracking-[0.14em] text-muted-foreground/70 uppercase">
          <CalendarDays className="size-3.5" /> Where I'm going
        </p>
        <h1 className="font-display text-4xl leading-[1.05] tracking-tight">Your calendar</h1>
        <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
          The rooms you approved — not everything on in the city. Approve something on
          the{" "}
          <Link to="/feed" className="text-primary underline-offset-4 hover:underline">
            the feed
          </Link>{" "}
          and it lands here.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList>
            <TabsTrigger value="month">
              <CalendarDays /> Month
            </TabsTrigger>
            <TabsTrigger value="week">
              <Rows3 /> Week
            </TabsTrigger>
            <TabsTrigger value="list">
              <List /> List
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {view === "month" && (
          <div className="flex items-center gap-1">
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Previous month"
              onClick={() => setCursor((c) => shiftMonth(c.year, c.month, -1))}
            >
              <ChevronLeft />
            </Button>
            <span className="min-w-40 text-center text-sm font-medium">{monthName}</span>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Next month"
              onClick={() => setCursor((c) => shiftMonth(c.year, c.month, 1))}
            >
              <ChevronRight />
            </Button>
          </div>
        )}

        {view === "week" && (
          <div className="flex items-center gap-1">
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Previous week"
              onClick={() => setWeekAnchor((iso) => shiftDays(iso, -7))}
            >
              <ChevronLeft />
            </Button>
            <span className="min-w-40 text-center text-sm font-medium">
              {rangeLabel(week[0]!, week[6]!)}
            </span>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Next week"
              onClick={() => setWeekAnchor((iso) => shiftDays(iso, 7))}
            >
              <ChevronRight />
            </Button>
          </div>
        )}
      </div>

      {picked.isError ? (
        <Card>
          <CardContent className="text-sm text-destructive">
            Could not read your calendar: {picked.error.message}
          </CardContent>
        </Card>
      ) : view === "month" ? (
        <MonthView cells={cells} grouped={grouped} today={today} />
      ) : view === "week" ? (
        <WeekView week={week} grouped={grouped} today={today} />
      ) : (
        <ListView events={events} today={today} />
      )}

      {/* One honest empty state, not three. It says the same true thing whichever
          view she is in: nothing approved yet is not the same as a quiet month. */}
      {!picked.isLoading && events.length === 0 && (
        <Card>
          <CardContent className="space-y-3">
            <h2 className="font-display text-lg">Nothing approved yet.</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              This fills up from the radar — every room you approve appears here, with
              the date and the venue. It is deliberately empty until you choose
              something.
            </p>
            <Button asChild size="sm">
              <Link to="/feed">Find rooms</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Local-date day stepping, at noon so a DST boundary cannot move the day. */
function shiftDays(iso: string, by: number): string {
  return new Date(new Date(`${iso}T12:00:00Z`).getTime() + by * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

function rangeLabel(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const a = new Date(`${from}T12:00:00Z`).toLocaleDateString(undefined, opts);
  const b = new Date(`${to}T12:00:00Z`).toLocaleDateString(undefined, opts);
  return `${a} – ${b}`;
}

function timeOf(event: { startsAt?: string | null }): string | null {
  // Only when the stamp actually carries a time. A date-only event rendered as
  // "00:00" claims a precision the feed never gave.
  return event.startsAt && event.startsAt.length > 10 ? event.startsAt.slice(11, 16) : null;
}

type PickedEvent = {
  id: string;
  name: string;
  date?: string | null;
  startsAt?: string | null;
  location?: string | null;
  url?: string | null;
};

function MonthView({
  cells,
  grouped,
  today,
}: {
  cells: ReturnType<typeof monthGrid>;
  grouped: Map<string, PickedEvent[]>;
  today: string;
}) {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[680px]">
        <div className="grid grid-cols-7 gap-px">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="px-2 pb-2 text-2xs tracking-[0.1em] text-muted-foreground/70 uppercase"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/40">
          {cells.map((cell) => {
            const dayEvents = grouped.get(cell.iso) ?? [];
            return (
              <div
                key={cell.iso}
                className={cn(
                  "min-h-24 space-y-1 bg-card/70 p-1.5 backdrop-blur-sm transition-colors",
                  // The neighbouring months are shown so the grid is rectangular,
                  // and dimmed so they are not mistaken for this month.
                  !cell.inMonth && "bg-card/25 text-muted-foreground/60",
                  cell.iso === today && "bg-primary/[0.07]",
                )}
              >
                <div className="flex items-center justify-between px-0.5">
                  <span
                    className={cn(
                      "text-xs tabular-nums",
                      cell.iso === today &&
                        "flex size-5 items-center justify-center rounded-full bg-primary text-2xs font-semibold text-primary-foreground",
                    )}
                  >
                    {cell.day}
                  </span>
                  {dayEvents.length > 2 && (
                    <span className="text-2xs text-muted-foreground">
                      {dayEvents.length}
                    </span>
                  )}
                </div>

                {/* Two pills then a count. A cell that grows with its contents
                    makes every other row in the month taller, and the month view
                    is for weight at a glance — the week and list views are where
                    the detail lives. */}
                {dayEvents.slice(0, 2).map((event) => (
                  <a
                    key={event.id}
                    href={event.url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    title={event.name}
                    className="block truncate rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-2xs leading-tight text-foreground hover:border-primary/50"
                  >
                    {timeOf(event) && (
                      <span className="mr-1 tabular-nums text-muted-foreground">
                        {timeOf(event)}
                      </span>
                    )}
                    {event.name}
                  </a>
                ))}
                {dayEvents.length > 2 && (
                  <p className="px-1 text-2xs text-muted-foreground">
                    +{dayEvents.length - 2} more
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekView({
  week,
  grouped,
  today,
}: {
  week: string[];
  grouped: Map<string, PickedEvent[]>;
  today: string;
}) {
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[680px] grid-cols-7 gap-2">
        {week.map((iso) => {
          const dayEvents = grouped.get(iso) ?? [];
          const date = new Date(`${iso}T12:00:00Z`);
          return (
            <div key={iso} className="space-y-2">
              <div
                className={cn(
                  "flex items-baseline gap-1.5 px-1",
                  iso === today && "text-primary",
                )}
              >
                <span className="text-2xs tracking-[0.1em] text-muted-foreground/70 uppercase">
                  {date.toLocaleDateString(undefined, { weekday: "short" })}
                </span>
                <span className="text-sm font-medium tabular-nums">{date.getUTCDate()}</span>
              </div>

              {dayEvents.length === 0 ? (
                <div className="min-h-16 rounded-lg border border-dashed border-border/50" />
              ) : (
                dayEvents.map((event) => (
                  <Card key={event.id} className="rounded-lg">
                    <CardContent className="space-y-1 p-2">
                      {timeOf(event) && (
                        <p className="text-2xs tabular-nums text-muted-foreground">
                          {timeOf(event)}
                        </p>
                      )}
                      <a
                        href={event.url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-sm leading-snug hover:text-primary"
                      >
                        {event.name}
                      </a>
                      {event.location && (
                        <p className="truncate text-2xs text-muted-foreground" title={event.location}>
                          {event.location}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Ordered by date, which is the only ordering that makes sense for something
 *  she cannot reorder. Grouped under day headings so a run of events on one
 *  evening reads as one evening. */
function ListView({ events, today }: { events: PickedEvent[]; today: string }) {
  const days = useMemo(() => {
    const grouped = byDay(events);
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  if (!days.length) return null;

  return (
    <div className="space-y-5">
      {days.map(([iso, dayEvents]) => (
        <section key={iso} className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <span className={cn(iso === today && "text-primary")}>
              {new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </span>
            {iso === today && <Badge variant="fund">today</Badge>}
          </h2>

          <div className="space-y-2">
            {dayEvents.map((event) => (
              <Card key={event.id}>
                <CardContent className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-3.5">
                  <span className="w-11 shrink-0 text-sm tabular-nums text-muted-foreground">
                    {timeOf(event) ?? "—"}
                  </span>
                  <a
                    href={event.url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="group min-w-0 flex-1 font-display text-base leading-snug hover:text-primary"
                  >
                    {event.name}
                    <ExternalLink className="ml-1.5 inline size-3 opacity-0 transition-opacity group-hover:opacity-60" />
                  </a>
                  {event.location && (
                    <span
                      className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
                      title={event.location}
                    >
                      <MapPin className="size-3 shrink-0 opacity-60" />
                      <span className="truncate">{event.location}</span>
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
